// adCache — download each ad video once and play it from the TV's own storage.
//
// A waiting-room TV loops the same handful of ads all day. Streaming them from
// the CDN on every rotation re-downloads them endlessly and ties playback to
// the hospital's network. Instead each video is fetched once into the
// browser's Cache Storage (on disk, survives reloads) and handed to <video> as
// a local blob: URL; the URL → blob: URL map below is the in-memory index so a
// rotation never re-reads the file.
//
// Everything here is best-effort. Any failure (no Cache API on an old TV
// browser, CDN without CORS so fetch() is refused, quota exceeded, a file too
// big) just leaves the ad playing from its normal network URL as before.
import { getAdResources } from "../api/resources";

const CACHE_NAME = "doctrue-ads-v1";
const MAX_BYTES = 200 * 1024 * 1024; // skip anything larger than 200 MB

const local = new Map<string, string>(); // network URL → blob: URL
const inflight = new Map<string, Promise<void>>();

const hasCacheApi = () =>
  typeof window !== "undefined" && "caches" in window && !!window.caches;

// The URL to hand to <video>: the local copy when we have one, else the
// network URL. Synchronous on purpose — an ad must never wait for a download.
export const playableUrl = (url: string): string => local.get(url) ?? url;

export const isLocalUrl = (src: string) => src.startsWith("blob:");

// A local copy failed to play (corrupt download, blob storage evicted): drop it
// so the next rotation streams from the network and re-caches.
export const forgetLocal = async (url: string) => {
  const blobUrl = local.get(url);
  if (blobUrl) {
    local.delete(url);
    URL.revokeObjectURL(blobUrl);
  }
  if (!hasCacheApi()) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.delete(url);
  } catch {
    /* best effort */
  }
};

const materialise = async (url: string): Promise<void> => {
  if (local.has(url) || !hasCacheApi()) return;
  const cache = await caches.open(CACHE_NAME);
  let res = await cache.match(url);
  if (!res) {
    // mode: "cors" so a CDN without CORS headers fails loudly here (caught by
    // the caller) instead of yielding an opaque, unreadable response.
    const fresh = await fetch(url, { mode: "cors" });
    if (!fresh.ok) throw new Error(`HTTP ${fresh.status}`);
    const len = Number(fresh.headers.get("content-length") ?? 0);
    if (len > MAX_BYTES) throw new Error(`too large (${len} bytes)`);
    try {
      await cache.put(url, fresh.clone());
    } catch (err) {
      // Out of quota on a small TV: still usable for this session from the
      // body we already have, just not persisted.
      console.warn("[queue-v2] ad cache put failed:", err);
    }
    res = fresh;
  }
  const blob = await res.blob();
  if (blob.size === 0) throw new Error("empty body");
  if (!local.has(url)) local.set(url, URL.createObjectURL(blob));
};

// Download the given videos one at a time (never fighting the ad that is
// playing for bandwidth) and drop cached files that are no longer in the
// hospital's ad list so removed ads don't pile up on the TV's disk.
export const prefetchAdVideos = (urls: string[]) => {
  if (!hasCacheApi()) return;
  const wanted = new Set(urls);
  const run = async () => {
    for (const url of urls) {
      if (local.has(url)) continue;
      if (!inflight.has(url)) {
        const p = materialise(url)
          .catch((err) => {
            console.warn("[queue-v2] ad video not cached, streaming instead:", url, err);
          })
          .finally(() => inflight.delete(url));
        inflight.set(url, p);
      }
      await inflight.get(url);
    }
    try {
      const cache = await caches.open(CACHE_NAME);
      for (const req of await cache.keys()) {
        if (!wanted.has(req.url)) await cache.delete(req);
      }
    } catch {
      /* best effort */
    }
  };
  run().catch(() => {});
};


// Warm the cache before the display is even open — called from the profile
// picker the TV lands on after login, so the first ad on screen already plays
// from local storage. Needs the access token to have landed (the resources
// API is authenticated); the caller gates on that.
export const prefetchHospitalAdVideos = async (hospitalId: string) => {
  if (!hasCacheApi()) return;
  try {
    const list = await getAdResources(hospitalId);
    prefetchAdVideos(
      list
        .filter((r) => r.category?.toLowerCase().includes("video"))
        .map((r) => r.resource_url)
    );
  } catch (err) {
    console.warn("[queue-v2] ad prefetch skipped:", err);
  }
};
